import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AiMessage from "./AiMessage";
import Icon from "./Icon";
import { authenticationApi } from "../../features/authentication";
import "../../styles/ai/ai-variables.css";
import "../../styles/ai/ai-chatbox.css";
import "../../styles/ai/ai-search.css";
import "../../styles/ai/ai-shared-chat.css";

const noop = () => {};

// A shared chat is a transcript, not a live conversation: the widgets that
// would send a new turn (answer chips, the area editor, the priority picker,
// relaxation offers) are dropped rather than shown inert.
function readOnly(msg) {
    const {
        onboardingOptions, onboardingAreaPins, onboardingAwaitingConfirmation,
        isPriorityPicker, priorityPickerItems, relaxationOptions, ...rest
    } = msg;
    return rest;
}

/** Public, read-only view of a conversation someone shared (/shared/:token). */
export default function SharedChatPage() {
    const { token } = useParams();
    const [state, setState] = useState({ status: "loading" });

    useEffect(() => {
        let cancelled = false;
        authenticationApi.getSharedChat(token)
            .then((chat) => !cancelled && setState({ status: "ready", chat }))
            .catch(() => !cancelled && setState({ status: "missing" }));
        return () => { cancelled = true; };
    }, [token]);

    const messages = useMemo(
        () => (state.chat?.messages || []).filter((m) => m && (m.text || m.properties?.length)).map(readOnly),
        [state.chat],
    );

    if (state.status === "loading") {
        return <div className="shared-chat-page"><div className="shared-chat-status">Loading conversation…</div></div>;
    }

    if (state.status === "missing") {
        return (
            <div className="shared-chat-page">
                <div className="shared-chat-status">
                    <Icon name="link" size={22} />
                    <h2>This conversation isn't available</h2>
                    <p>The link may be wrong, or the owner stopped sharing it.</p>
                    <Link className="shared-chat-cta" to="/ai-search">Start your own search</Link>
                </div>
            </div>
        );
    }

    const sharedOn = new Date(state.chat.sharedAt).toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric",
    });

    return (
        <div className="shared-chat-page">
            <header className="shared-chat-header">
                <div className="shared-chat-badge">
                    <Icon name="share" size={13} />
                    Shared conversation · {sharedOn}
                </div>
                <h1>{state.chat.title}</h1>
            </header>

            <div className="shared-chat-messages chat-messages-container">
                {messages.map((msg, i) => (
                    <AiMessage
                        key={msg.id || `msg-${i}`}
                        msg={msg}
                        chatId={`shared-${token}`}
                        index={i}
                        onEdit={noop}
                        onRetry={noop}
                        onCopy={noop}
                        onFollowUp={noop}
                        onMapView={noop}
                        onRoadMapView={noop}
                        isMapOpen={false}
                        prevMessages={messages.slice(0, i)}
                    />
                ))}
            </div>

            <footer className="shared-chat-footer">
                <p>Find homes that match what you actually need.</p>
                <Link className="shared-chat-cta" to="/ai-search">
                    <Icon name="sparkle" size={15} />
                    Try OGM AI
                </Link>
            </footer>
        </div>
    );
}
